import { useId, useMemo, useState } from "react";
import { formatMoney } from "../../../api/money";
import type { CataloguePart } from "./readerApi";

interface Props {
  /** The supplier's own words for the line, which seed the search. */
  quoted: string;
  catalogue: CataloguePart[];
  onPart: (part: CataloguePart) => void;
  onSundry: () => void;
}

/** How many matches are offered at once: enough to see the right one, few
 * enough to read. A narrower search finds the rest. */
const SHOWN = 5;

/** The first word of the supplier's description worth searching on -- "PILOT
 * TUBING KIT SS 10MM" starts the search at "pilot" -- so the likely part is
 * usually already in the list when the picker opens. */
export function seedFor(quoted: string): string {
  const word = quoted
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .find((token) => token.length >= 4 && !/^\d+(mm)?$/.test(token));
  return word ?? "";
}

/** The parts whose code, description and size contain every word typed. */
export function matchParts(catalogue: CataloguePart[], query: string): CataloguePart[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [];
  }
  return catalogue.filter((part) => {
    const text = `${part.code} ${part.description} ${part.size}`.toLowerCase();
    return words.every((word) => text.includes(word));
  });
}

/** What an unmatched line is: a search over the supplier's price list with a
 * short list of matches, or "not a catalogue part". It replaces a single
 * `<select>` that held the whole price list -- hundreds of options, read one
 * by one (Fix #14). */
export default function PartPicker({ quoted, catalogue, onPart, onSundry }: Props) {
  const [query, setQuery] = useState(() => seedFor(quoted));
  const id = useId();
  const matches = useMemo(() => matchParts(catalogue, query), [catalogue, query]);
  const shown = matches.slice(0, SHOWN);

  return (
    <div className="qr-picker" role="group" aria-label="Choose what this line is">
      <span className="qr-picker-ask">Not in the price list. What is it?</span>
      <input
        type="search"
        className="input"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search the price list by code or description"
        aria-label="Search the price list"
        aria-controls={id}
      />
      <div id={id} className="qr-matches">
        {query.trim() === "" ? (
          <p className="qr-picker-hint">Type part of a code or a description to search {catalogue.length} parts.</p>
        ) : shown.length === 0 ? (
          <p className="qr-picker-hint">Nothing in the price list matches “{query.trim()}”.</p>
        ) : (
          shown.map((part) => (
            <button key={part.spare_part_id ?? part.code} type="button" className="qr-match" onClick={() => onPart(part)}>
              <span className="mono">{part.code}</span>
              <span>
                {part.description}
                {part.size && part.size !== "NO SIZE" ? ` — ${part.size}` : ""}
              </span>
              <span className="qr-match-price">{formatMoney(part.unit_cost)}</span>
            </button>
          ))
        )}
        {matches.length > SHOWN ? (
          <p className="qr-picker-hint">
            {matches.length - SHOWN} more match; narrow the search to see them.
          </p>
        ) : null}
      </div>
      <button type="button" className="row-button" onClick={onSundry}>
        Not a catalogue part — keep as quoted
      </button>
    </div>
  );
}
